import { useState, useRef, useEffect, useCallback } from "react";

/**
 * Email/Message form data interface
 * Used across all message composition components
 */
export interface NewMessageFormData {
  messageType: string;
  messageTypeValue: any;
  context: string;
  emailText: string;
  to: any[];
  cc: any[];
  notify: any[];
  subject: string;
  from: string;
  fromValue: any;
  mailBox: any;
  emailContent: string;
  editor: string;
  participants: any[];
  phoneStatus: string;
}

/**
 * Default form state for new message
 */
export const getDefaultFormState = (
  initialNotifyUsers: any[] = []
): NewMessageFormData => ({
  messageType: "",
  messageTypeValue: "",
  context: "",
  emailText: "",
  to: [],
  cc: [],
  notify: initialNotifyUsers,
  subject: "",
  from: "",
  fromValue: "",
  mailBox: {},
  emailContent: "",
  editor: "",
  participants: [],
  phoneStatus: "262144",
});

/**
 * Hook for managing email/message form state
 * Provides form data, ref for async operations, and update methods
 */
export const useNewMessageForm = (initialNotifyUsers: any[] = []) => {
  const [formData, setFormDataState] = useState<NewMessageFormData>(
    getDefaultFormState(initialNotifyUsers)
  );

  // Ref for accessing current form state in async callbacks
  const formStateRef = useRef<NewMessageFormData>(formData);

  // Keep ref in sync with state
  useEffect(() => {
    formStateRef.current = formData;
  }, [formData]);

  /**
   * Update form data with partial updates
   */
  const setFormData = useCallback(
    (
      updates:
        | Partial<NewMessageFormData>
        | ((prev: NewMessageFormData) => NewMessageFormData)
    ) => {
      if (typeof updates === "function") {
        setFormDataState(updates);
      } else {
        setFormDataState((prev) => ({ ...prev, ...updates }));
      }
    },
    []
  );

  /**
   * Reset form to initial state
   */
  const resetForm = useCallback(() => {
    setFormDataState(getDefaultFormState(initialNotifyUsers));
  }, [initialNotifyUsers]);

  /**
   * Create params handler for filter components
   * Converts Set values to arrays and updates form
   */
  const createParams = useCallback(
    (filterTypeName: string, filterValue: any) => {
      setFormDataState((prev) => ({
        ...prev,
        [filterTypeName]: filterValue instanceof Set ? Array.from(filterValue) : filterValue,
      }));
    },
    []
  );

  return {
    formData,
    formStateRef,
    setFormData,
    resetForm,
    createParams,
  };
};
